import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { AppLink } from '../components/AppLink';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Icon } from '../components/Icon';
import { Picture } from '../components/Picture';
import { ConsultationBanner } from '../components/services/ConsultationBanner';
import { ProcessSection } from '../components/services/ProcessSection';
import { materials, pricingTiers, services, type Service } from '../data/services';
import { Seo, breadcrumbLd } from '../lib/seo';
import { absoluteUrl } from '../lib/site';
import { container, eyebrow, h2Section } from '../lib/ui';

const STATS: Array<[value: string, label: string]> = [
  ['140+', 'Turnkey Residencies'],
  ['98.4%', 'Milestone Precision'],
  ['18 Mo.', 'Bespoke Warranty'],
  ['100%', 'In-House Millwork'],
];

const itemListLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Interior design services by Green Valley Interiors',
  itemListElement: services.map((s, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: s.name,
    url: absoluteUrl(`/services/${s.slug}`),
  })),
};

function DisciplinePanel({ service, id, labelledBy, hidden }: { service: Service; id: string; labelledBy: string; hidden: boolean }) {
  const s = service;
  return (
    <div
      role="tabpanel"
      id={id}
      aria-labelledby={labelledBy}
      hidden={hidden}
      tabIndex={0}
      className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-dark rounded-xl"
    >
      <div className="lg:col-span-6 flex flex-col gap-space-md">
        <div className="inline-flex items-center gap-2 text-secondary font-label-sm text-label-sm uppercase tracking-widest">
          <Icon name={s.discipline.icon} size={16} />
          <span>
            Discipline {s.discipline.number} // {s.discipline.label}
          </span>
        </div>
        <h3 className="font-headline-md text-headline-md text-primary">{s.headline}</h3>
        <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{s.intro}</p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-sm pt-space-xs">
          {s.facts.map((f) => (
            <div key={f.label} className="p-space-sm bg-surface-ivory rounded shadow-sm">
              <dt className="font-label-md text-label-md uppercase tracking-wider text-primary mb-1">{f.label}</dt>
              <dd className="font-body-sm text-body-sm text-on-surface-variant">{f.value}</dd>
            </div>
          ))}
        </dl>
        <div className="space-y-2 pt-2">
          <p className="font-label-sm text-label-sm uppercase tracking-widest text-primary">Included Architectural Deliverables</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-body-sm text-body-sm text-on-surface-variant">
            {s.deliverables.map((d) => (
              <li key={d} className="flex items-center gap-2">
                <Icon name="check_circle" size={16} className="text-emerald-vivid" /> {d}
              </li>
            ))}
          </ul>
        </div>
        <div className="pt-space-sm flex flex-wrap items-center gap-x-space-lg gap-y-space-sm">
          <AppLink
            href={`/services/${s.slug}`}
            className="inline-flex items-center gap-2 text-primary font-label-lg text-label-lg uppercase tracking-wider group hover:text-emerald-vivid transition-colors"
          >
            <span>Explore {s.name}</span>
            <Icon name="north_east" size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
          </AppLink>
          <AppLink
            href="/contact"
            className="inline-flex items-center gap-1 text-secondary font-label-md text-label-md uppercase tracking-wider hover:text-primary transition-colors"
          >
            {s.briefCta}
            <Icon name="arrow_forward" size={14} />
          </AppLink>
        </div>
      </div>
      <div className="lg:col-span-6 relative">
        <div className="relative w-full h-[340px] sm:h-[440px] lg:h-[520px] rounded-xl overflow-hidden shadow-xl bg-surface-container">
          <Picture
            name={s.image}
            alt={s.imageAlt}
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="block w-full h-full"
            imgClassName="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/70 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-6 left-6 right-6 p-4 rounded bg-surface-ivory/80 backdrop-blur-md shadow-md flex items-center justify-between gap-space-sm">
            <div>
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary block">{s.featured.label}</span>
              {s.featured.project ? (
                <AppLink
                  href={`/portfolio/${s.featured.project}`}
                  className="font-headline-sm text-headline-sm text-primary hover:text-secondary transition-colors"
                >
                  {s.featured.title}
                </AppLink>
              ) : (
                <span className="font-headline-sm text-headline-sm text-primary">{s.featured.title}</span>
              )}
            </div>
            <Icon name={s.featured.icon} size={24} className="text-primary" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  const [active, setActive] = useState(0);
  const baseId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const tabId = (i: number) => `${baseId}-tab-${i}`;
  const panelId = (i: number) => `${baseId}-panel-${i}`;

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const last = services.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = active === last ? 0 : active + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = active === 0 ? last : active - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <>
      <Seo
        title="Interior Design Services, Nagercoil"
        description="Residential, office, hospitality, modular kitchen and bespoke joinery services from Green Valley Interiors, a turnkey interior design studio in Nagercoil."
        keywords={[
          'interior design services in Nagercoil',
          'turnkey interior design company in Kanyakumari',
          'luxury villa interior packages in Tamil Nadu',
          'commercial and office interior services Nagercoil',
          'interior design cost for villas in Nagercoil',
          'interior designers serving Trivandrum and Kerala',
        ]}
        path="/services"
        jsonLd={[breadcrumbLd([['Services', '/services']]), itemListLd]}
      />

      {/* HERO */}
      <section className="relative w-full overflow-hidden bg-surface-ivory pt-space-lg pb-space-xl md:pb-20">
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-gold-light/20 blur-3xl pointer-events-none" />
        <div className="absolute left-1/4 -bottom-24 w-80 h-80 rounded-full bg-emerald-vivid/5 blur-3xl pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <Breadcrumbs trail={[['Services', '/services']]} className="mb-space-lg" />
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-space-lg mb-space-xl">
            <h1 className="max-w-3xl flex flex-col items-start gap-space-md animate-rise">
              <span className="inline-flex items-center gap-space-xs px-3 py-1 rounded bg-surface-cream text-secondary shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                <span className="font-label-sm text-label-sm uppercase tracking-widest">Interior Design Services in Nagercoil</span>
              </span>
              <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary tracking-tight">
                Tailored Spatial Disciplines &amp; <span className="italic font-normal text-secondary">Turnkey Mastery</span>
              </span>
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-md lg:pb-space-xs animate-rise [animation-delay:80ms]">
              Where structural austerity harmonizes with tactile indulgence. We orchestrate uncompromising private
              retreats and high-stakes commercial environments with bespoke artisan joinery.
            </p>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-space-md py-space-md px-space-md md:px-space-lg bg-surface-container-lowest/80 backdrop-blur-md rounded-xl shadow-sm">
            {STATS.map(([value, label]) => (
              <div key={label} className="flex flex-col-reverse justify-end">
                <dt className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">{label}</dt>
                <dd className="font-headline-md text-headline-md text-primary font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section className="w-full py-space-xl md:py-20 bg-surface" id="disciplines">
        <div className={container}>
          <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-space-md mb-space-xl">
            <div>
              <span className={`${eyebrow} block mb-space-xs`}>Pillars of Practice</span>
              <h2 className={h2Section}>Core Architectural Portfolios</h2>
            </div>
            <div
              role="tablist"
              aria-label="Service disciplines"
              className="flex flex-wrap items-center gap-2 p-1.5 bg-surface-container rounded-xl"
            >
              {services.map((s, i) => {
                const selected = i === active;
                return (
                  <button
                    key={s.slug}
                    ref={(el) => {
                      tabRefs.current[i] = el;
                    }}
                    type="button"
                    role="tab"
                    id={tabId(i)}
                    aria-selected={selected}
                    aria-controls={panelId(i)}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => setActive(i)}
                    onKeyDown={onTabKey}
                    className={`px-4 py-2 rounded font-label-md text-label-md uppercase tracking-wider transition-all duration-300 ${
                      selected ? 'bg-primary text-gold-light shadow-sm' : 'text-on-surface-variant hover:text-primary'
                    }`}
                  >
                    {s.tabLabel}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="relative">
            {services.map((s, i) => (
              <DisciplinePanel key={s.slug} service={s} id={panelId(i)} labelledBy={tabId(i)} hidden={i !== active} />
            ))}
          </div>
        </div>
      </section>

      <ProcessSection />

      {/* PRICING */}
      <section className="w-full py-space-xl md:py-20 bg-surface" id="tiers">
        <div className={container}>
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-space-md mb-space-xl">
            <div>
              <span className={`${eyebrow} block mb-space-xs`}>Predictable Engagements</span>
              <h2 className={h2Section}>Transparent Scope &amp; Tiers</h2>
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-md">
              Calibrated engagement models structured for complete financial transparency and uncompromising craft
              execution.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-gutter items-stretch">
            {pricingTiers.map((t) =>
              t.highlighted ? (
                <div
                  key={t.title}
                  className="reveal relative p-space-lg bg-primary text-on-primary rounded-xl shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between overflow-hidden"
                >
                  <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-emerald-vivid/20 blur-2xl pointer-events-none" />
                  <div className="relative z-10">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-space-md">
                      <span className="inline-flex items-center gap-2 px-3 py-1 rounded bg-primary-container text-gold-light font-label-sm text-label-sm uppercase tracking-wider">
                        {t.badge}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-secondary text-on-secondary font-label-sm text-label-sm uppercase tracking-widest">
                        <Icon name="star" size={12} /> Most Requested
                      </span>
                    </div>
                    <h3 className="font-headline-md text-headline-md text-gold-light mb-1">{t.title}</h3>
                    <p className="font-body-sm text-body-sm text-surface-container-highest/80 mb-space-md">{t.text}</p>
                    <p className="mb-space-lg">
                      <span className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-on-primary font-medium">
                        {t.price}
                      </span>{' '}
                      <span className="font-body-sm text-body-sm text-surface-container-highest/80">{t.priceNote}</span>
                    </p>
                    <ul className="space-y-3 font-body-sm text-body-sm text-surface-container-highest">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Icon name="check_circle" size={18} className="text-gold-light mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative z-10 mt-space-lg pt-space-md">
                    <AppLink
                      href="/contact"
                      className="w-full py-3 px-4 rounded bg-gold-light text-primary hover:bg-surface-cream text-center font-label-md text-label-md uppercase tracking-wider block transition-colors shadow-md font-semibold"
                    >
                      {t.cta}
                    </AppLink>
                  </div>
                </div>
              ) : (
                <div
                  key={t.title}
                  className="reveal p-space-lg bg-surface-ivory rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded bg-surface-container text-on-surface-variant font-label-sm text-label-sm uppercase tracking-wider mb-space-md">
                      {t.badge}
                    </span>
                    <h3 className="font-headline-md text-headline-md text-primary mb-1">{t.title}</h3>
                    <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">{t.text}</p>
                    <p className="mb-space-lg">
                      <span className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-primary font-medium">
                        {t.price}
                      </span>{' '}
                      <span className="font-body-sm text-body-sm text-on-surface-variant">{t.priceNote}</span>
                    </p>
                    <ul className="space-y-3 font-body-sm text-body-sm text-on-surface-variant">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Icon name="check" size={18} className="text-secondary mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-space-lg pt-space-md">
                    <AppLink
                      href="/contact"
                      className="w-full py-3 px-4 rounded bg-surface-container text-primary hover:bg-primary hover:text-gold-light text-center font-label-md text-label-md uppercase tracking-wider block transition-colors shadow-sm"
                    >
                      {t.cta}
                    </AppLink>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* MATERIALS */}
      <section className="w-full py-space-xl md:py-20 bg-botanical-dark text-on-primary">
        <div className={container}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center">
            <div className="reveal lg:col-span-5 flex flex-col gap-space-sm">
              <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light">The Guild Difference</span>
              <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-on-primary">
                Materials of Noble Lineage &amp; Uncompromised Density
              </h2>
              <p className="font-body-md text-body-md text-surface-container-highest/80 leading-relaxed">
                We avoid composite veneers and engineered facades wherever possible. Our spaces are built from
                deep-veined raw quartzite, air-dried teak timbers cured across seasons, and heavy patinated brass that ages
                gracefully alongside its owners.
              </p>
              <ul className="pt-space-sm flex flex-col gap-2">
                <li className="flex items-center gap-3 p-3 rounded bg-primary-container/40">
                  <Icon name="eco" size={22} className="text-gold-light" />
                  <span className="font-body-sm text-body-sm text-surface-container-highest">
                    FSC-Certified sustainably harvested Indian teak &amp; American Walnut
                  </span>
                </li>
                <li className="flex items-center gap-3 p-3 rounded bg-primary-container/40">
                  <Icon name="texture" size={22} className="text-gold-light" />
                  <span className="font-body-sm text-body-sm text-surface-container-highest">
                    Direct quarry block extraction with consecutive bookmatching
                  </span>
                </li>
              </ul>
            </div>
            <ul className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-space-sm">
              {materials.map((m) => (
                <li key={m.name} className="reveal group relative rounded-lg overflow-hidden bg-primary-container h-40 sm:h-48">
                  <Picture
                    name={m.image}
                    alt={m.alt}
                    sizes="(min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw"
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-botanical-dark/50 flex flex-col justify-end p-3">
                    <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light">{m.kind}</span>
                    <span className="font-body-sm text-body-sm text-on-primary font-medium">{m.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <ConsultationBanner />
    </>
  );
}
